import AppModal from "@/components/AppModal";
import {
  CARD_STRING_TYPE as CARD_TYPE,
  CARD_TRANS_REFUND_STATUS,
  CARD_TRANS_STATUS,
  CARD_TRANSACTION_SHOW_STATUS,
  CARD_TRANSACTION_TYPE,
  DEDUCT_TYPE
} from "@/constant/Constant";
import useToggle from "@/hook/useToggle";
import useTransAuthRules from "@/hook/useTransAuthRules";
import CollapseButton from "@/pages/components/CollapseButton";
import SimpleCurrencyAmount from "@/pages/components/SimpleCurrencyAmount";
import { isTransDeclineExamption, isTransSmallExamption } from "@/utils/AssetsUtils";
import {
  Stack, styled, Divider, dividerClasses, Typography, Stepper, Step, StepLabel,
  stepConnectorClasses, stepLabelClasses, useTheme, tableCellClasses
} from "@mui/material";
import React, {useEffect, useMemo} from "react";
import { useTranslation } from "react-i18next";
import RpIcon from "@/components/RpIcon.jsx";
import SimpleHeader from "@/pages/components/Headers/SimpleHeader";
import { useConfirm } from "material-ui-confirm";
import {useBoolean} from "ahooks";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import {LoadingButton} from "@mui/lab";
import useSettings from "@/hook/useSettings.jsx";

const StyledPaymentList = styled(Stack)(({theme}) => ({
  padding: '8px 0',
  '.title': {
    padding: '4px 0',
    fontSize: '16px',
    color: theme.palette.text.primary,
  }
}));

const StyledDeclinedTrans = styled(Stack)(({theme}) => ({
  borderRadius: '8px',
  border: `solid 1px ${theme.palette.text.disabled}`,
  borderTop: 'none',
  overflow: 'visible',
  position: 'relative',
  '.arrow': {
    position: 'absolute',
    top: '-5px',
    left: '44px',
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderWidth: '0 7px 7px 7px',
    borderColor: `transparent transparent ${theme.palette.error.main} transparent`,
  },
  '.reason': {
    backgroundColor: theme.palette.error.main,
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    padding: '8px',
    color: '#fff',
    fontSize: '12px'
  },
  '.reason-fee-wrapper': {
    padding: '8px',
    '.span': {
      padding: '8px 0',
      color: theme.palette.text.secondary,
    }
  }
}));

const StyledFeeTitle = styled('div')(({theme}) => ({
  color: theme.palette.text.secondary,
  padding: '10px 0',
}));

const StyledFeeDisplay = styled(Stack)(({theme}) => ({
  padding: '4px 0',
  justifyContent: 'space-between',
  '.title': {
    color: theme.palette.text.secondary,
    fontSize: '14px',
  },
  '.value': {
    color: theme.palette.text.primary,
    fontSize: '14px',
    '.exemption-value': {
      color: theme.palette.text.disabled,
      padding: '0 6px',
      textDecoration: 'line-through',
    }
  }
}));


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '10px',
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.background.card,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

export const _renderSmallAuthRule = (item, authRule) => {
  const { t } = useTranslation();
  const title = t('small_auth_fee_stats');
  const theme = useTheme()

  const ruleItem = useMemo(() => {
    if (!authRule) {
      return {}
    }
    return authRule?.ruleItems.find(_ => _.type == 0)
  }, [authRule]);

  return <>
    <Stack>
      <Stack
        sx={{
          backgroundColor: theme.palette.background.card,
          margin: '0 16px',
          padding: '12px 16px',
        }}
      >
        <img />
        <Typography
          sx={{fontSize: '12px'}}
        ><span style={{color: theme.palette.text.secondary}} >{t('card_number')}</span> .... {item?.last4}</Typography>
        <Typography
          sx={{fontSize: '12px'}}
        >Small transactions this month <span style={{ fontWeight: 'bold' }}>{authRule?.authTotalCount}</span></Typography>
        <Typography
          sx={{fontSize: '12px'}}
        >The remaining number of small transaction number this month <span style={{fontWeight: 'bold'}}>{authRule?.authTotalCount}</span>/{ruleItem?.freeCount}</Typography>
      </Stack>
      <Stack sx={{margin: '24px 16px 24px 16px'}}>
        <Typography
          sx={{fontWeight: 'bold', fontSize: '12px'}}
        >{t('rules')}</Typography>
        <Typography
          sx={{color: theme.palette.text.secondary,  fontSize: '12px'}}
        >
          Failed transactions will increase the number of bad card records，and additional fees will be added if you exceed them. Please regulate the transaction.
        </Typography>
        <TableContainer sx={{margin: '12px 0'}}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Item</StyledTableCell>
                <StyledTableCell align="right">Free Count</StyledTableCell>
                <StyledTableCell align="right">Fees</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                '& .MuiTableCell-root': {
                  fontSize: '12px'
                }
              }}
            >
              {authRule?.ruleItems.map((row) => (
                <TableRow
                  key={row.name}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell
                    sx={{
                      width: '120px',
                      color: row.type == 1 ? theme.palette.brand.main : theme.palette.warning.main
                    }}
                    component="th" scope="row">
                    {`${t('small_trade_fee')} ${row.name}`}
                  </TableCell>
                  <TableCell align="right">{row.freeCount}/{row.freeCountUnit}</TableCell>
                  <TableCell align="right">{row.fee} {row.feeUnit}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <LoadingButton fullWidth variant="contained">{t('got_it')}</LoadingButton>
      </Stack>

    </Stack>
  </>;
}

const _declineTransInfo = ({record, _onShowDeclinedAuthFeeTip}) => {

  const t = useTranslation().t;
  let declineExemption = isTransDeclineExamption(record);

  let transDeduction = record.transactionDeduct;
  // if (!transDeduction?.realDeductOrderList?.leng             th) return null;

  const paymentItem = transDeduction?.realDeductOrderList[0];

  const lineThrough = useMemo(() => {
    if (record?.transactionDeduct?.freeCharge && record?.transactionDeduct.realDeductOrderList?.length <= 0) {
      return true
    }
    return false
  }, [record.transactionDeduct])

  return (
    <>
      <StyledDeclinedTrans sx={{marginTop:'10px'}}>
        <span className="arrow"></span>
        <div className="reason">{record.declineReason}</div>
        <div className="reason-fee-wrapper">
          <div className={'span'}>{t('declined_transaction_fee')}<RpIcon
            clickable
            onClick={() => {
              _onShowDeclinedAuthFeeTip()
            }}
            icon="info" size={12} /></div>
          <Stack direction={'row'} spacing={1}>
            <Stack spacing={1}>
              {
                transDeduction?.freeCharge ?
                  <SimpleCurrencyAmount lineThrough={lineThrough} amount={`-${transDeduction.deductAmount}`} currencyCode={transDeduction.deductCurrency} />
                  :
                  null
              }
              {transDeduction?.realDeductOrderList.map((item, idx) =>
                <SimpleCurrencyAmount lineThrough={lineThrough} key={idx} amount={`-${item.amount}`} currencyCode={item.varietyCode} />)}
            </Stack>
          </Stack>
        </div>
      </StyledDeclinedTrans>
    </>
  )
}

export default function TransPaymentInfo({ record, cardType }) {
  const { t } = useTranslation();
  const {
    toggle: isCollapsed,
    onToggle: onToggleIsCollapsed,
  } = useToggle(false);
  const {
    toggle: authRuleShow,
    onToggle: onToggleAuthRuleShow,
    onClose
  } = useToggle(false);


  const {settings} = useSettings()

  const [smallAuthRuleShow, smallAuthRuleShowOperate] = useBoolean(false)

  const confirm = useConfirm();
  const theme = useTheme();
  const {
    authRule,
    renderAuthRule,
    // _renderSmallAuthRule,
    _renderDeclineAuthRule,
    setCardId,
  } = useTransAuthRules();

  useEffect(() => {
    if (record.cardId) {
      setCardId(record.cardId);
    }
  }, [record.cardId])

  const _onShowSmallAuthFeeTip = () => {
    if (authRule) {
      smallAuthRuleShowOperate.setTrue();
      return;
    }
  }

  const _onShowDeclinedAuthFeeTip = () => {
    if (authRule) {
      onToggleAuthRuleShow();
      return;
    }
    setCardId(record.cardId);
  }

  const _collapseBtn = () => {
    return <CollapseButton collapse={isCollapsed} onClick={onToggleIsCollapsed} />
  }

  const _onShowFeeTip = (feeCode) => {
    //   // {/*feeCode 枚举 FxFee\AtmFee\RemitFee\SmallAuthFee\DeclineFee\MccFee*/}
    switch (feeCode) {
      case 'FxFee':
        confirm({
          title: t('fx_fee'),
          content: t('fx_fee_desc'),
          hideCancelButton: true,
          confirmationText: t('got_it')
        })
        break
      case 'AtmFee':
        confirm({
          title: t('atm_fee'),
          content: t('atm_fee_desc'),
          hideCancelButton: true,
          confirmationText: t('got_it')
        })
        break
      case 'RemitFee':
        confirm({
          title: t('remit_fee'),
          content: t('remit_fee_desc'),
          hideCancelButton: true,
          confirmationText: t('got_it')
        })
        break
      case 'SmallAuthFee':
        _onShowSmallAuthFeeTip()
        break
      case 'MccFee':
        confirm({
          title: t('MccFee'),
          content: t('mcc_fee_desc'),
          hideCancelButton: true,
          confirmationText: t('got_it')
        })
        break
      default:

        break
    }
    t('fx_fee');
    t('fx_fee_desc');
  }

  const _onShowATMFeeTip = () => {
    // todo: popup confirm.
    t('atm_fee');
    t('atm_fee_desc');
  }

  const _onShowAppendFeeTip = () => {
    confirm({
      title: t('incremental'),
      content: t('incremental_desc'),
      hideCancelButton: true,
      confirmationText: t('got_it')
    })
  }

  // if (!record) return null;

  let isSmallExemption = isTransSmallExamption(record);

  let transDeduction = record.transactionDeduct;
  let realFee;

  // 是否有特殊商户收费 realDeductType
  let deductionItem = transDeduction?.realDeductOrderList?.length ? 
    transDeduction.realDeductOrderList[0] : null;

  if (deductionItem) {
    realFee = transDeduction.realDeductOrderList[0];
  }

  // 卡授权费
  const authPaymentList = record.paymentOrderList;
  const hasAuthPayment = !!authPaymentList?.length;


  // 追加明细数据
  const appendPaymentList = record.paymentRecoverList;

  // 退款明细数据
  const refundPaymentList = record.paymentRefundList;

  // 无关联退款数据
  const singleRefund = record.singleRefundReceived;

  // ATM fee
  const atmFeeAmount = record.transactionFee?.atmFeeAmount;
  const atmFeeCurrency = record.transactionFee?.atmFeeCurrency;

  // FX fee
  const fxFeeAmount = record.transactionFee?.fxFeeAmount;
  const fxFeeCurrency = record.transactionFee?.fxFeeCurrency;

  // {/*feeCode 枚举 FxFee\AtmFee\RemitFee\SmallAuthFee\DeclineFee\MccFee*/}
  // record.feeList = [
  //   {
  //     feeCode: 'FxFee',
  //     feeName: 'feeName',
  //     varietyCode: 'usdt'
  //   },
  //   {
  //     feeCode: 'AtmFee',
  //     feeName: 'feeName',
  //     varietyCode: 'usdt'
  //   },
  //   {
  //     feeCode: 'RemitFee',
  //     feeName: 'feeName',
  //     varietyCode: 'usdt'
  //   },
  //   {
  //     feeCode: 'SmallAuthFee',
  //     feeName: 'feeName',
  //     varietyCode: 'usdt'
  //   },
  //   {
  //     feeCode: 'DeclineFee',
  //     feeName: 'feeName',
  //     varietyCode: 'usdt'
  //   }
  // ]
  const hasSmallAuthFee = record.deductType == DEDUCT_TYPE.SMALL && realFee;
  const showFees = !(record.status == CARD_TRANS_STATUS.DECLINED) && record.feeList.length > 0 // !(record.status == CARD_TRANS_STATUS.DECLINED) && hasSmallAuthFee || atmFeeAmount || fxFeeAmount; !(record.status == CARD_TRANS_STATUS.DECLINED) &&

  const isDeclined = record.transactionType == CARD_TRANSACTION_TYPE.CARD_TRANSACTION && record.transactionShowStatus == CARD_TRANSACTION_SHOW_STATUS.DECLINED;

  // if (record.transactionType == CARD_TRANSACTION_TYPE.CARD_TRANSACTION && record.transactionShowStatus == CARD_TRANSACTION_SHOW_STATUS.DECLINED) {
  //   return _declineTransInfo();
  // }

  // if (isCollapsed) return _collapseBtn();

  const authRuleItem = useMemo(() => {
    if (!authRule) {
      return {}
    }
    return authRule?.ruleItems.find(_ => _.type == 0)
  }, [authRule]);

  const declineRuleItem = useMemo(() => {
    if (!authRule) {
      return {}
    }
    return authRule?.ruleItems.find(_ => _.type == 1)
  }, [authRule]);

  const hasReversal = useMemo(() => {
    return record?.hasReversal
  }, [record])

  const hasRefund = useMemo(() => {
    return record?.hasRefund
  }, [record])

  const _cardImg = useMemo(() => {

    // if (cardType == CARD_TYPE.VIRTUAL) {

    //   return `/images/cards/card-virtual${
    //     settings.theme === "light" ? "-light" : ""
    //   }.png`;
    // }
    // return `/images/cards/card-physical${
    //   settings.theme === "light" ? "-light" : ""
    // }.png`;
    return '/images/sonic/sonic-card-mini.png'
  }, [cardType]);

  return (
    <>
      {
        !isCollapsed && <>
        {
          record.declineReason &&
          <_declineTransInfo record={record} _onShowDeclinedAuthFeeTip={_onShowDeclinedAuthFeeTip} />
        }

          <Stack>
            {/* 无关联退款 */}
            {singleRefund && <StyledPaymentList>
              <div className="title">{t('received')}</div>
              {!isCollapsed && <SimpleCurrencyAmount amount={singleRefund.amount} currencyCode={singleRefund.varietyCode} />}
            </StyledPaymentList>}

            {/* 卡片授权支付币种列表 */}
            {hasAuthPayment && <StyledPaymentList>
              <Divider
                sx={{
                  '&:before': {
                    display: 'none',
                  },
                  [`& .${dividerClasses.wrapper}`]: {
                    paddingLeft: 0
                  }
                }}
                textAlign="left">
                <Stack sx={{color: theme.palette.text.secondary}} direction="row" alignItems="center" className="title">
                  <RpIcon color={theme.palette.text.secondary} icon="change" size={16} />
                  <Typography sx={{fontSize: '12px', color: theme.palette.text.secondary, lineHeight: 1}}>{t('card_authorization_payment')}</Typography>
                </Stack>
              </Divider>
              {!isCollapsed ? authPaymentList.map((item, idx) =>
                <SimpleCurrencyAmount key={idx} amount={`-${item.amount}`} currencyCode={item.varietyCode} />
              ) : null}
            </StyledPaymentList>}

            {/* 退款 */}
            {!isCollapsed &&
              <>
                {/*{*/}
                {/*  !!refundPaymentList.length && <StyledPaymentList>*/}
                {/*    /!*<div className="title">{t('refund')}</div>*!/*/}
                {/*    <Divider*/}
                {/*      sx={{*/}
                {/*        marginBottom: '0px',*/}
                {/*        '&:before': {*/}
                {/*          display: 'none',*/}
                {/*        },*/}
                {/*        [`& .${dividerClasses.wrapper}`]: {*/}
                {/*          paddingLeft: 0*/}
                {/*        }*/}
                {/*      }}*/}
                {/*      textAlign="left">*/}
                {/*      <Stack direction="row" alignItems="center">*/}
                {/*        <RpIcon color={theme.palette.text.secondary} icon="arrow-down" size={14} />*/}
                {/*        <Typography sx={{fontSize: '12px', color: theme.palette.text.secondary, lineHeight: 1}}>Revceived</Typography>*/}
                {/*      </Stack>*/}
                {/*    </Divider>*/}
                {/*    {refundPaymentList.map((item, idx) =>*/}
                {/*      <SimpleCurrencyAmount key={idx} amount={item.amount} currencyCode={item.varietyCode} />*/}
                {/*    )}*/}

                {/*  </StyledPaymentList>*/}
                {/*}*/}
                {
                  record.transactionType == CARD_TRANSACTION_TYPE.VISA_TRANSFER &&
                  <Stack>
                    <Stepper
                      sx={{
                        [`& .${stepConnectorClasses.root}`]: {
                          marginLeft: '12px'
                        },
                        [`& .${stepConnectorClasses.line}`]: {
                          height: '8px',
                          minHeight: 'unset'
                        }
                      }}
                      orientation="vertical">
                      <Step>
                        <StepLabel
                          sx={{
                            paddingBottom: '0',
                            [`& .${stepLabelClasses.label}`]: {
                              fontSize: '12px'
                            }
                          }}
                          StepIconComponent={() => {
                            return <RpIcon icon="a-Property1Outline15-24PXProperty2ddian-copy" color={theme.palette.warning.main} size={26} />
                          }}
                        >{t('bank_verifying')}</StepLabel>
                      </Step>
                      <Step>
                        <StepLabel
                          sx={{
                            paddingTop: '0',
                            [`& .${stepLabelClasses.label}`]: {
                              fontSize: '12px'
                            }
                          }}
                          StepIconComponent={() => {
                            return <RpIcon color={theme.palette.text.tertiary} icon="a-Property1Outline15-24PXProperty2weijingguodian" size={26} />
                          }}
                        >{t('estimate_time')}</StepLabel>
                      </Step>
                    </Stepper>
                  </Stack>
                }
              </>}

            {!isCollapsed && showFees &&
              <Stack>
                <Divider
                  sx={{
                    marginTop: '14px',
                    '&:before': {
                      display: 'none',
                    },
                    [`& .${dividerClasses.wrapper}`]: {
                      paddingLeft: 0
                    }
                  }}
                  textAlign="left">
                  <Stack direction="row" alignItems="center">
                    <RpIcon color={theme.palette.text.secondary} icon="a-Property1Outline15-24PXProperty2yuandianzhanwei" size={14} />
                    <Typography sx={{fontSize: '12px', color: theme.palette.text.secondary, lineHeight: 1}}>{t('fees_included')}</Typography>
                  </Stack>
                </Divider>
                <Stack direction="row">
                  <div style={{width: '14px'}}>

                  </div>
                  <Stack sx={{width: '100%'}}>
                    {record.feeList.map(_ => (
                      <StyledFeeDisplay direction={'row'} justifyContent="space-between">
                        <Stack direction="row" alignItems="center" className="title" justifyContent="space-between">
                          {/*feeCode 枚举 FxFee\AtmFee\RemitFee\SmallAuthFee\DeclineFee\MccFee*/}
                          <Typography sx={{fontSize: '12px', color: theme.palette.text.secondary, lineHeight: 1}}>{t(_.feeCode)}</Typography>
                          <RpIcon color={theme.palette.text.secondary} clickable icon="question-circle" size={14} onClick={() => {
                            _onShowFeeTip(_.feeCode)
                          }}/>
                        </Stack>
                        <Stack direction="row" alignItems="center" gap={'4px'}>
                          <Typography
                            sx={{
                              textDecoration: _?.freeCharge ? 'line-through' : 'none',
                            }}
                            style={{flex: 1, textAlign: 'right'}} className="value">{_.feeAmount} {_.varietyCode}</Typography>
                          {_?.freeCharge && <Typography
                            sx={{

                            }}
                             className="value">0.00 {_.varietyCode}</Typography>}
                        </Stack>
                      </StyledFeeDisplay>
                    ))}
                  </Stack>
                </Stack>
              </Stack>
            }

            {/* 追加支付 */}
            {!isCollapsed && !!appendPaymentList.length && <StyledPaymentList>
              <Divider
                sx={{
                  marginTop: '14px',
                  '&:before': {
                    display: 'none',
                  },
                  [`& .${dividerClasses.wrapper}`]: {
                    paddingLeft: 0
                  }
                }}
                textAlign="left">
                <Stack direction="row" alignItems="center">
                  <RpIcon color={theme.palette.text.secondary} icon="book-add" size={14} />
                  <Typography sx={{fontSize: '12px', color: theme.palette.text.secondary, lineHeight: 1}}>{t('incremental')}</Typography>
                  <RpIcon color={theme.palette.text.secondary} clickable icon="question-circle" size={14} onClick={() => {
                    _onShowAppendFeeTip()
                  }}/>
                </Stack>
              </Divider>
              {/*<div className="title">{t('incremental')}<RpIcon color={theme.palette.text.secondary} clickable icon="question-circle" size={14} onClick={() => {*/}
              {/*  _onShowAppendFeeTip()*/}
              {/*}}/></div>*/}
              {appendPaymentList.map((item, idx) =>
                <SimpleCurrencyAmount key={idx} amount={`-${item.amount}`} currencyCode={item.varietyCode} />
              )}
            </StyledPaymentList>}

            {
              hasReversal && <Stack>
                <Divider
                  sx={{
                    marginTop: '14px',
                    '&:before': {
                      display: 'none',
                    },
                    [`& .${dividerClasses.wrapper}`]: {
                      paddingLeft: 0
                    }
                  }}
                  textAlign="left">
                  <Stack direction="row" alignItems="center">
                    <RpIcon color={theme.palette.text.secondary} icon="cancel" size={14} />
                    <Typography sx={{fontSize: '12px', color: theme.palette.text.secondary, lineHeight: 1}}>{t('card_trans_status_reversal')}</Typography>
                  </Stack>
                </Divider>
                <StyledPaymentList>
                  {record.paymentReversalList.map((item, idx) =>
                    <SimpleCurrencyAmount key={idx} amount={`+${item.amount}`} currencyCode={item.varietyCode} />
                  )}
                </StyledPaymentList>
              </Stack>
            }

            {
              hasRefund && <Stack>
                <Divider
                  sx={{
                    marginTop: '14px',
                    '&:before': {
                      display: 'none',
                    },
                    '&:after': {
                      borderColor: theme.palette.warning.main
                    },
                    [`& .${dividerClasses.wrapper}`]: {
                      paddingLeft: 0,

                    }
                  }}
                  textAlign="left">
                  <Stack direction="row" alignItems="center">
                    <RpIcon color={theme.palette.warning.main} icon="arrow-left" size={14} />
                    <Typography sx={{fontSize: '12px', color: theme.palette.warning.main, lineHeight: 1}}>{t('refund')}</Typography>
                  </Stack>
                </Divider>
                <StyledPaymentList>
                  {record.paymentRefundList.map((item, idx) =>
                    <SimpleCurrencyAmount key={idx} amount={`+${item.amount}`} currencyCode={item.varietyCode} />
                  )}
                </StyledPaymentList>
              </Stack>
            }

            {/*{_collapseBtn()}*/}
            <CollapseButton collapse={isCollapsed} onClick={onToggleIsCollapsed} />
          </Stack>
          <AppModal open={smallAuthRuleShow} onClose={smallAuthRuleShowOperate.setFalse}>
            <Stack>
              <SimpleHeader
                sx={{
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px'
                }}
                title={t('small_auth_transaction_fee_stas')}
                closeButton={
                  <RpIcon icon="close" clickable
                          onClick={() => {
                            smallAuthRuleShowOperate.setFalse()
                          }} />
                }
                backEnable
              />
            </Stack>
            <Stack>
              <Stack
                sx={{
                  backgroundColor: theme.palette.background.card,
                  margin: '0 16px',
                  padding: '12px 16px',
                }}
              >
                <img />
                <Stack
                  sx={{fontSize: '12px'}}
                  direction="row"
                  alignItems="center"
                >
                  <img style={{width: '24px', height: '14px'}} src={_cardImg}/>
                  <span style={{color: theme.palette.text.secondary}} >{t('card_number')}</span> .... {record?.last4}</Stack>
                <Typography
                  sx={{fontSize: '12px'}}
                >{t('small_transactions_this_month')} <span style={{ fontWeight: 'bold' }}>{authRule?.authTotalCount}</span></Typography>
                <Typography
                  sx={{fontSize: '12px'}}
                >{t('small_autn_trans_remain')} <span style={{fontWeight: 'bold'}}>{Math.min(authRule?.authTotalCount, authRuleItem?.freeCount)}</span>/{authRuleItem?.freeCount}</Typography>
              </Stack>
              <Stack sx={{margin: '24px 16px 24px 16px'}}>
                <Typography
                  sx={{fontWeight: 'bold', fontSize: '12px'}}
                >{t('rules')}</Typography>
                <Typography
                  sx={{color: theme.palette.text.secondary,  fontSize: '12px'}}
                >
                  {t('failed_rules')}
                </Typography>
                <TableContainer sx={{margin: '12px 0'}}>
                  <Table aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>{t('rule_item')}</StyledTableCell>
                        <StyledTableCell align="right">{t('free_count')}</StyledTableCell>
                        <StyledTableCell align="right">{t('fees')}</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody
                      sx={{
                        '& .MuiTableCell-root': {
                          fontSize: '12px'
                        }
                      }}
                    >
                      {authRule?.ruleItems.map((row) => (
                        <TableRow
                          key={row.name}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell
                            sx={{
                              width: '120px',
                              color: row.type == 1 ? theme.palette.brand.main : theme.palette.warning.main
                            }}
                            component="th" scope="row">
                            {`${t('small_trade_fee')} ${row.name}`}
                          </TableCell>
                          <TableCell align="right">{row.freeCount}/{row.freeCountUnit}</TableCell>
                          <TableCell align="right">{row.fee} {row.feeUnit}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <LoadingButton onClick={() => {
                  smallAuthRuleShowOperate.setFalse()
                }} fullWidth variant="contained">{t('got_it')}</LoadingButton>
              </Stack>

            </Stack>
          </AppModal>
          <AppModal open={authRuleShow} onClose={onToggleAuthRuleShow}>
            <Stack>
              <SimpleHeader
                sx={{
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px'
                }}
                title={t('declined_transaction_fee_stats')}
                closeButton={
                  <RpIcon icon="close" clickable
                          onClick={() => {
                            // _onShowSmallAuthFeeTip()
                            onToggleAuthRuleShow();
                          }} />
                }
                backEnable
              />
            </Stack>
            <Stack>
              <Stack
                sx={{
                  backgroundColor: theme.palette.background.card,
                  margin: '0 16px',
                  padding: '12px 16px',
                }}
              >
                <img />
                <Stack
                  sx={{fontSize: '12px'}}
                  direction="row"
                  alignItems="center"
                >
                  <img style={{width: '24px', height: '14px'}} src={_cardImg}/>
                  <span style={{color: theme.palette.text.secondary}}>{t('card_number')}</span> .... {record?.last4}
                </Stack>
                <Typography
                  sx={{fontSize: '12px'}}
                >{t('declined_transactions_this_month')} <span style={{ fontWeight: 'bold' }}>{authRule?.declinedTotalCount}</span></Typography>
                <Typography
                  sx={{fontSize: '12px'}}
                >{t('declined_trans_remain')} <span style={{fontWeight: 'bold'}}>{Math.min(authRule?.declinedTotalCount, declineRuleItem?.freeCount)}</span>/{declineRuleItem?.freeCount}</Typography>
              </Stack>
              <Stack sx={{margin: '24px 16px 24px 16px'}}>
                <Typography
                  sx={{fontWeight: 'bold', fontSize: '12px'}}
                >{t('rules')}</Typography>
                <Typography
                  sx={{color: theme.palette.text.secondary,  fontSize: '12px'}}
                >{t('declined_rules')}
                  </Typography>
                <TableContainer sx={{margin: '12px 0'}}>
                  <Table aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>{t('rule_item')}</StyledTableCell>
                        <StyledTableCell align="right">{t('free_count')}</StyledTableCell>
                        <StyledTableCell align="right">{t('fees')}</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody
                      sx={{
                        '& .MuiTableCell-root': {
                          fontSize: '12px'
                        }
                      }}
                    >
                      {authRule?.ruleItems.map((row) => (
                        <TableRow
                          key={row.name}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell
                            sx={{
                              width: '120px',
                              color: row.type == 1 ? theme.palette.brand.main : theme.palette.warning.main
                            }}
                            component="th" scope="row">
                            {
                              row.type == 0 ?
                              `${t('small_trade_fee')} ($0-$${row.amountLimit})`
                              : row.name
                            }
                          </TableCell>
                          <TableCell align="right">{row.freeCount}/{row.freeCountUnit}</TableCell>
                          <TableCell align="right">{row.fee} {row.feeUnit}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <LoadingButton onClick={() => {
                  onToggleAuthRuleShow()
                }}  fullWidth variant="contained">{t('got_it')}</LoadingButton>
              </Stack>

            </Stack>
          </AppModal>
        </>
      }
      {
        isCollapsed && <>
          {_collapseBtn()}
        </>
      }
    </>
  )
}
